import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  onIdTokenChanged, signInWithEmailAndPassword, signOut as fbSignOut, setPersistence,
  browserLocalPersistence, browserSessionPersistence, type User,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { usernameToEmail, type CompactClaims, type UserTemplate } from '@shared/permissions.ts';

export interface UserProfile {
  uid: string;
  username: string;
  displayName: string;
  active: boolean;
  template: UserTemplate;
  claimsVersion?: number;
}

interface AuthState {
  loading: boolean;
  user: User | null;
  claims: CompactClaims | null;
  profile: UserProfile | null;
  signIn: (username: string, password: string, remember: boolean) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

function pickClaims(c: Record<string, unknown>): CompactClaims | null {
  if (typeof c.p !== 'string') return null;
  return { a: c.a === 1 ? 1 : 0, p: c.p, m: Array.isArray(c.m) ? (c.m as string[]) : [], x: c.x === 1 ? 1 : 0, ...(c.t === 1 ? { t: 1 as const } : {}) };
}

/**
 * Auth + claims. When an admin changes someone's permissions, onUserWritten bumps
 * users/{uid}.claimsVersion; we listen to it and force a token refresh so the change applies
 * right away (docs/PERMISSIONS.md).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<CompactClaims | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const seenVersion = useRef<number | null>(null);

  useEffect(() => onIdTokenChanged(auth, async (u) => {
    setUser(u);
    if (!u) { setClaims(null); setProfile(null); setLoading(false); seenVersion.current = null; return; }
    const res = await u.getIdTokenResult();
    setClaims(pickClaims(res.claims));
    setLoading(false);
  }), []);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'users', user.uid), async (snap) => {
      if (!snap.exists()) { setProfile(null); return; }
      const d = snap.data();
      setProfile({
        uid: user.uid, username: d.username, displayName: d.displayName, active: d.active === true,
        template: d.template, claimsVersion: d.claimsVersion,
      });
      const v = typeof d.claimsVersion === 'number' ? d.claimsVersion : 0;
      if (seenVersion.current !== null && v !== seenVersion.current) {
        await user.getIdToken(true); // triggers onIdTokenChanged → new claims
      }
      seenVersion.current = v;
    }, () => setProfile(null));
  }, [user]);

  const value = useMemo<AuthState>(() => ({
    loading, user, claims, profile,
    async signIn(username, password, remember) {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      await signInWithEmailAndPassword(auth, usernameToEmail(username), password);
    },
    async signOut() {
      // the home page's last summary (financial) stays on this device only while signed in
      try { Object.keys(localStorage).filter((k) => k.startsWith('dashboard.')).forEach((k) => localStorage.removeItem(k)); } catch { /* private mode */ }
      await fbSignOut(auth);
    },
  }), [loading, user, claims, profile]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}
